import type { ChipsProps } from 'primereact/chips';

type PtPart = object | ((options: unknown) => object) | undefined;

const merge = (own: PtPart, attrs: object): PtPart => {
  if (typeof own === 'function') return (options: unknown) => ({ ...attrs, ...own(options) });
  return { ...attrs, ...own };
};

export interface ChipsAccessibleName {
  /** Accessible name for the chip group; also set on the text field by the caller. */
  label?: string;
  /** Id of the element that names the chip group. */
  labelledBy?: string;
  /** Whether the chips are disabled; Chips marks only its text field. */
  disabled?: boolean;
}

/**
 * Completes the accessibility PrimeReact's Chips (10.9) leaves unfinished.
 *
 * Chips renders `<ul role="listbox">` holding one `<li role="option">` per token, followed by
 * `<li class="p-chips-input-token">` wrapping the text field new chips are typed into. That
 * markup cannot be made valid with the listbox model: a `listbox` may only own `option`
 * children, but the text field is a sibling of the options, so every render reports
 * `aria-required-children` (and `role="listbox"` replaces the `<ul>`'s native `list` role,
 * stranding its `<li>` children without a list). The listbox is also left with no accessible
 * name, because Chips applies `aria-label` / `aria-labelledby` to the text field only.
 *
 * The chips are a labelled group of removable text tokens followed by an input, so this
 * describes them that way:
 * - the container is a `group` carrying the accessible name the caller supplies;
 * - a disabled group says so (`aria-disabled`): Chips disables the text field alone and dims
 *   the whole control, so the tokens otherwise read as enabled text below contrast;
 * - tokens and the input wrapper are presentational (`none`), so their text still reads in
 *   order and the `<input>` keeps its own textbox role and stays focusable. The option-only
 *   `aria-label` / `aria-selected` / set-position attributes are dropped with the role, since
 *   they are not valid on a presentational element.
 *
 * A caller's own pass-through wins.
 */
export function chipsAccessibility(pt: ChipsProps['pt'], name: ChipsAccessibleName = {}): ChipsProps['pt'] {
  const callerPt = (pt ?? {}) as Record<string, PtPart>;
  return {
    ...callerPt,
    container: merge(callerPt.container, {
      role: 'group',
      'aria-orientation': undefined,
      'aria-label': name.label,
      'aria-labelledby': name.labelledBy,
      'aria-disabled': name.disabled ? 'true' : undefined,
    }),
    token: merge(callerPt.token, {
      role: 'none',
      'aria-label': undefined,
      'aria-selected': undefined,
      'aria-setsize': undefined,
      'aria-posinset': undefined,
    }),
    inputToken: merge(callerPt.inputToken, { role: 'none' }),
  } as ChipsProps['pt'];
}
